import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { rdb } from "@/services/config";
import { ref as rdbRef, onValue } from "firebase/database";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { usePermissions } from "@/hooks";
import { useUser, useNotification, useLoading } from "@/contexts";
import { chatService, PublicPhoto } from "@/services";
import { ModalAlert, UserStatus, WaitingApproval } from "@/components";
import { noPhoto, slugify } from "@/utils";
import EmojiPicker, { Theme } from "emoji-picker-react";
import "./Messages.css";

const formatTime = (timestamp: any) => {
  if (!timestamp) return "";
  const date = timestamp.toDate();
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

interface ConversationItemProps {
  conv: any;
  currentUserId: string | undefined;
  activeChatId: string | null;
  isSubscriber: boolean;
  onSelectChat: (id: string) => void;
}

interface ChatMainProps {
  activeChatId: string;
  userData: any;
  conversations: any[] | undefined;
  messages: any[] | undefined;
  isSubscriber: boolean;
  onCloseChat: () => void;
  setIsAlertOpen: (open: boolean) => void;
}

interface ChatInputProps {
  isPending: boolean;
  isSubscriber: boolean;
  onSend: (text: string) => Promise<void>;
  setIsAlertOpen: (open: boolean) => void;
}

const ConversationItem: React.FC<ConversationItemProps> = React.memo(
  ({ conv, currentUserId, activeChatId, isSubscriber, onSelectChat }) => {
    const otherId = useMemo(() => {
      return Object.keys(conv.participantData).find(
        (id) => id !== currentUserId,
      );
    }, [conv.participantData, currentUserId]);

    const participant = useMemo(() => {
      return otherId ? conv.participantData[otherId] : null;
    }, [conv.participantData, otherId]);

    const participantImage = useMemo(() => {
      if (!participant) return noPhoto(undefined);

      const imgData = participant.profileImage as PublicPhoto | string;
      if (!imgData) return noPhoto(participant.gender);

      if (typeof imgData === "string" && imgData.trim()) {
        return imgData;
      }

      if (
        typeof imgData === "object" &&
        imgData.approved !== false &&
        imgData.url?.trim()
      ) {
        return imgData.url;
      }

      return noPhoto(participant.gender);
    }, [participant]);

    const unread = useMemo(() => {
      return currentUserId ? (conv.unreadCount?.[currentUserId] ?? 0) : 0;
    }, [conv.unreadCount, currentUserId]);

    const isActive = activeChatId === conv.id;

    return (
      <div
        className={`chat-item ${isActive ? "active" : ""} ${conv.status === "pending" ? "pending-style" : ""}`}
        onClick={() => onSelectChat(conv.id)}
      >
        <img
          className={!isSubscriber ? "blur-image" : ""}
          src={participantImage}
          alt={participant?.username || "Avatar"}
        />
        <div className="chat-item-info">
          <div className="chat-item-header">
            <strong className={!isSubscriber ? "blur-text" : ""}>
              {participant?.username || "Participante"}
              {conv.status === "pending" && (
                <span className="badge-pending">Pendente</span>
              )}
            </strong>
            <span className="time">{formatTime(conv.lastUpdate)}</span>
          </div>
          <div className="chat-item-footer">
            <p className={!isSubscriber ? "masked-last-message" : ""}>
              {isSubscriber ? conv.lastMessage : "Assine para ler a mensagem"}
            </p>
            {unread > 0 && !isActive && (
              <span className="chat-unread-chip">{unread}</span>
            )}
          </div>
        </div>
      </div>
    );
  },
);

const ChatMain: React.FC<ChatMainProps> = ({
  activeChatId,
  userData,
  conversations,
  messages,
  isSubscriber,
  onCloseChat,
  setIsAlertOpen,
}) => {
  const navigate = useNavigate();
  const { withLoading } = useLoading();

  const [presence, setPresence] = useState<{
    state: string;
    lastActivity?: any;
  } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const activeChatData = useMemo(
    () => conversations?.find((c) => c.id === activeChatId),
    [conversations, activeChatId],
  );

  const otherUser = useMemo(() => {
    if (!activeChatData || !userData?.uid) return null;
    const otherId = Object.keys(activeChatData.participantData).find(
      (id) => id !== userData.uid,
    );
    return otherId
      ? { ...activeChatData.participantData[otherId], uid: otherId }
      : null;
  }, [activeChatData, userData?.uid]);

  const otherUserProfileImage = useMemo(() => {
    if (!otherUser) return noPhoto(undefined);

    const imgData = otherUser.profileImage as PublicPhoto | string;
    if (!imgData) return noPhoto(otherUser.gender);

    if (typeof imgData === "string" && imgData.trim()) {
      return imgData;
    }

    if (
      typeof imgData === "object" &&
      imgData.approved !== false &&
      imgData.url?.trim()
    ) {
      return imgData.url;
    }

    return noPhoto(otherUser.gender);
  }, [otherUser]);

  useEffect(() => {
    if (!otherUser?.uid) return;

    const statusRef = rdbRef(rdb, `status/${otherUser.uid}`);

    const unsubscribe = onValue(statusRef, (snapshot) => {
      const value = snapshot.val();
      setPresence(value);
    });

    return () => unsubscribe();
  }, [otherUser?.uid]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = useCallback(
    async (messageText: string) => {
      if (!activeChatId || !userData?.uid || !activeChatData) return;

      const receiverId = activeChatData.users.find(
        (id: string) => id !== userData.uid,
      );

      await chatService.sendMessage(
        activeChatId,
        userData.uid,
        receiverId,
        messageText,
      );
    },
    [activeChatId, userData?.uid, activeChatData],
  );

  const handleAccept = async () => {
    await withLoading(async () => {
      try {
        await chatService.acceptChatRequest(activeChatId);
      } catch (err) {
        alert("Erro ao aceitar solicitação.");
      }
    });
  };

  if (!otherUser) return null;

  const isPending = activeChatData?.status === "pending";
  const iRequested = activeChatData?.requestedBy === userData?.uid;
  const iReceived = isPending && !iRequested;

  return (
    <main className="chat-main">
      <header className="chat-header">
        <div className="chat-header-left">
          <button
            className="mobile-back-btn"
            onClick={(e) => {
              e.stopPropagation();
              onCloseChat();
            }}
            aria-label="Voltar"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
          </button>
          <div
            className="chat-header-user"
            onClick={() => navigate(`/perfil/${slugify(otherUser.username)}`)}
          >
            <img
              className={!isSubscriber ? "blur-image" : ""}
              src={otherUserProfileImage}
              alt={otherUser.username}
            />
            <div className="user-details">
              <h3 className={!isSubscriber ? "blur-text" : ""}>
                {otherUser.username}
              </h3>
              <span className="status">
                {isPending ? (
                  "Solicitação de conversa"
                ) : (
                  <UserStatus
                    state={presence?.state || "offline"}
                    lastActivity={presence?.lastActivity}
                    showText={true}
                  />
                )}
              </span>
            </div>
          </div>
        </div>
      </header>

      <div
        className={`chat-messages ${!isSubscriber ? "blur-messages" : ""}`}
        ref={scrollRef}
        onClick={() => {
          if (!isSubscriber) setIsAlertOpen(true);
        }}
      >
        {messages?.map((msg: any, index: number) => {
          const isMe = msg.senderId === userData?.uid;

          return (
            <div
              key={`msg-${index}`}
              className={`message-row ${isMe ? "me" : "other"}`}
            >
              {!isMe && (
                <img
                  className={`message-avatar ${!isSubscriber ? "blur-image" : ""}`}
                  src={otherUserProfileImage}
                  alt={otherUser.username}
                />
              )}

              <div className="message-bubble">
                <div className="message-text">{msg.text}</div>
                <span className="message-time">
                  {formatTime(msg.createdAt)}

                  {isMe && (
                    <span className="message-status-ticks">
                      {msg.read ? (
                        <svg
                          className="tick-blue"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                        >
                          <path
                            fill="currentColor"
                            d="M0.41 13.41L6 19L11.59 13.41L13 14.83L6 21.83L0.41 16.24L0.41 13.41ZM6 16.17L1.83 12L0.41 13.41L6 19L17.59 7.41L16.17 6L6 16.17ZM23.59 7.41L22.17 6L12 16.17L10.83 15L9.41 16.41L12 19L23.59 7.41Z"
                          />
                        </svg>
                      ) : presence?.state === "online" ? (
                        <svg
                          className="tick-gray"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                        >
                          <path
                            fill="currentColor"
                            d="M0.41 13.41L6 19L11.59 13.41L13 14.83L6 21.83L0.41 16.24L0.41 13.41ZM6 16.17L1.83 12L0.41 13.41L6 19L17.59 7.41L16.17 6L6 16.17ZM23.59 7.41L22.17 6L12 16.17L10.83 15L9.41 16.41L12 19L23.59 7.41Z"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="tick-gray"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                        >
                          <path
                            fill="currentColor"
                            d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                          />
                        </svg>
                      )}
                    </span>
                  )}
                </span>
              </div>
            </div>
          );
        })}

        {isPending && (
          <div className="pending-notice-box">
            <p>
              {iRequested
                ? "Sua solicitação foi enviada. Aguarde o usuário aceitar."
                : `Um usuário enviou uma solicitação. Aceite para conversar.`}
            </p>
          </div>
        )}
      </div>

      <ChatInput
        isPending={isPending}
        isSubscriber={isSubscriber}
        onSend={handleSend}
        setIsAlertOpen={setIsAlertOpen}
      />
    </main>
  );
};

const ChatInput: React.FC<ChatInputProps> = React.memo(
  ({ isPending, isSubscriber, onSend, setIsAlertOpen }) => {
    const [text, setText] = useState("");
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!isSubscriber) {
        setIsAlertOpen(true);
        return;
      }
      if (!text.trim() || isPending) return;

      await onSend(text.trim());
      setText("");
    };

    return (
      <form className="chat-input-area" onSubmit={handleSubmit}>
        <div className="chat-input-wrapper">
          <button
            type="button"
            className="emoji-btn"
            onClick={() => setShowEmojiPicker((v) => !v)}
            disabled={isPending}
          >
            😊
          </button>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={isPending}
            placeholder={
              isPending ? "Chat bloqueado..." : "Digite sua mensagem..."
            }
            onClick={() => {
              if (!isSubscriber) setIsAlertOpen(true);
            }}
          />
          {showEmojiPicker && !isPending && (
            <div className="emoji-picker-container">
              <EmojiPicker
                style={{ backgroundColor: "#1f1f1f", borderColor: "#2e2e2e" }}
                theme={Theme.DARK}
                onEmojiClick={(emojiData) => {
                  setText((t) => t + emojiData.emoji);
                  setShowEmojiPicker(false);
                }}
                height={320}
              />
            </div>
          )}
        </div>
        <button
          type="submit"
          className="send-btn"
          disabled={isPending || !text.trim()}
        >
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </form>
    );
  },
);

const MessagesScreen: React.FC = () => {
  const { setActiveChatId: setGlobalActiveChat } = useNotification();
  const { userData } = useUser();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const isSubscriber =
    typeof hasPermission === "function"
      ? hasPermission("ACCESS_CHAT_AREA")
      : true;

  const query = useMemo(
    () =>
      userData?.uid ? chatService.getMyConversationsQuery(userData.uid) : null,
    [userData?.uid],
  );

  const [conversations] = useCollectionData(query);

  const [messages] = useCollectionData(
    activeChatId ? chatService.getMessagesQuery(activeChatId) : null,
  );

  useEffect(() => {
    setGlobalActiveChat(activeChatId);
    return () => setGlobalActiveChat(null);
  }, [activeChatId, setGlobalActiveChat]);

  useEffect(() => {
    if (activeChatId && userData?.uid) {
      chatService.markAsRead(activeChatId, userData.uid).catch(console.error);
    }
  }, [activeChatId, userData?.uid]);

  const conversationsMap = useMemo(() => {
    const map = new Map<string, any>();
    if (!conversations) return map;
    conversations.forEach((conv) => {
      map.set(conv.id, conv);
    });
    return map;
  }, [conversations]);

  const filteredConversationIds = useMemo(() => {
    if (!conversations) return [];

    const filtered = conversations.filter((conv: any) => {
      if (!searchTerm) return true;

      const otherId = Object.keys(conv.participantData).find(
        (id) => id !== userData?.uid,
      );
      const participant = otherId ? conv.participantData[otherId] : null;

      return participant?.username
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
    });

    return [...filtered]
      .sort((a, b) => {
        const unreadA = userData?.uid
          ? (a.unreadCount?.[userData.uid] ?? 0)
          : 0;
        const unreadB = userData?.uid
          ? (b.unreadCount?.[userData.uid] ?? 0)
          : 0;

        if (unreadA > 0 && unreadB === 0) return -1;
        if (unreadB > 0 && unreadA === 0) return 1;

        const timeA = a.lastUpdate?.toDate
          ? a.lastUpdate.toDate().getTime()
          : 0;
        const timeB = b.lastUpdate?.toDate
          ? b.lastUpdate.toDate().getTime()
          : 0;

        return timeB - timeA;
      })
      .map((c) => c.id as string);
  }, [conversations, searchTerm, userData?.uid]);

  const handleSelectChat = useCallback((id: string) => {
    setActiveChatId(id);
  }, []);

  const handleCloseChat = useCallback(() => {
    setActiveChatId(null);
  }, []);

  if (userData?.awaitingApproval) {
    return (
      <div className=" container">
        <WaitingApproval username={userData.username || ""} />
      </div>
    );
  }

  return (
    <div className={`messages-page  ${activeChatId ? "chat-open" : ""}`}>
      <aside className="chat-sidebar">
        <div className="sidebar-header">
          <h2>Mensagens</h2>
          <div className="search-wrapper">
            <input
              type="search"
              placeholder="Buscar ou começar uma nova conversa"
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="conversations-list">
          {filteredConversationIds.map((id) => {
            const conv = conversationsMap.get(id);
            if (!conv) return null;
            return (
              <ConversationItem
                key={id}
                conv={conv}
                currentUserId={userData?.uid}
                activeChatId={activeChatId}
                isSubscriber={isSubscriber}
                onSelectChat={handleSelectChat}
              />
            );
          })}
        </div>
      </aside>

      {activeChatId ? (
        <ChatMain
          activeChatId={activeChatId}
          userData={userData}
          conversations={conversations}
          messages={messages}
          isSubscriber={isSubscriber}
          onCloseChat={handleCloseChat}
          setIsAlertOpen={setIsAlertOpen}
        />
      ) : (
        <main className="chat-main empty-view">
          <div className="chat-empty-state">
            <h1>Suas Mensagens</h1>
            <p>Selecione uma conversa para começar.</p>
          </div>
        </main>
      )}

      <ModalAlert
        title="Você precisa ser assinante para enviar mensagens."
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onSubscribe={() => {
          navigate("/planos");
          setIsAlertOpen(false);
        }}
      />
    </div>
  );
};

export default MessagesScreen;
